import { useMutation, useQueryClient } from '@tanstack/react-query';
import { cacheKeys, useFetcher } from '../../graphql';

export interface SetWorkspaceEmailEnabledInput {
  workspaceId: string;
  enabled: boolean;
}

export interface SetWorkspaceEmailEnabledResult {
  workspaceId: string;
  emailEnabled: boolean;
  emailSlug: string | null;
}

interface SetWorkspaceEmailEnabledResponse {
  workspace: {
    setWorkspaceEmailEnabled: SetWorkspaceEmailEnabledResult;
  };
}

/**
 * Enable/disable email for a workspace (Communication settings — GRIPP #2968 /
 * plan 0024 Phase 1). Requires global admin on the backend (`*.*`); gate the UI
 * entry point with the same permission.
 */
export const useSetWorkspaceEmailEnabled = () => {
  const queryClient = useQueryClient();
  const fetcherFn = useFetcher<
    SetWorkspaceEmailEnabledResponse,
    { input: SetWorkspaceEmailEnabledInput }
  >(SET_WORKSPACE_EMAIL_ENABLED);

  return useMutation({
    mutationFn: async (input: SetWorkspaceEmailEnabledInput) => {
      const data = await fetcherFn({ input });
      return data.workspace.setWorkspaceEmailEnabled;
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({
        queryKey: cacheKeys('workspace').detail(result.workspaceId),
      });
    },
  });
};

const SET_WORKSPACE_EMAIL_ENABLED = `
  mutation SetWorkspaceEmailEnabled($input: SetWorkspaceEmailEnabledInput!) {
    workspace {
      setWorkspaceEmailEnabled(input: $input) {
        workspaceId
        emailEnabled
        emailSlug
      }
    }
  }
`;
